<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { RecipeParagraph } from '../../../utils/paragraph-schema/paragraphs'

const props = defineProps<{
  serving?: RecipeParagraph['recipe']['serving']
}>()

const emit = defineEmits<{
  servingChanged: [ratio: number]
}>()

const amount = ref(0)
const min = 1
const max = 999
const hint = ref('')

// falls back to 1 (no scaling) when serving.amount is missing or not positive
const servingRatio = computed(() => props.serving?.amount && props.serving.amount > 0 ? amount.value / props.serving.amount : 1)

// in case the serving amount is changed from outside, we need to update the local amount accordingly (preview)
watch(() => props.serving?.amount, (newAmount) => {
  amount.value = newAmount ?? 0
}, { immediate: true })

const handleChange = (action?: 'increment' | 'decrement'): void => {
  if (action === 'increment') {
    amount.value += 1
  } else if (action === 'decrement') {
    amount.value -= 1
  }

  if (amount.value < min) {
    amount.value = min
  } else if (amount.value > max) {
    amount.value = max
  }
  if (props.serving?.amount !== amount.value) {
    hint.value = 'Bitte beachte, dass sich Mengenangaben im Zubereitungstext auf die ursprüngliche Portionsgröße beziehen und nicht automatisch angepasst werden.'
  } else {
    hint.value = ''
  }
  emit('servingChanged', servingRatio.value)
}
</script>

<template>
  <div v-if="hint"
       class="bx-content bx-content__continuous-text bx-typo--center">
    <p v-if="hint">
      {{ hint }}
    </p>
  </div>
  <div class="bx-recipe__serving"
       data-tc="recipe-serving">
    <button class="icon icon-minus"
            @click.prevent="handleChange('decrement')">
      <svg class="bx-serving__icon">
        <use href="#minus" />
      </svg>
    </button>
    <input v-model.number="amount"
           type="number"
           :min="min"
           :max="max"
           :title="`Anzahl ${serving?.unit?.name}`"
           :aria-label="`Anzahl ${serving?.unit?.name}`"
           @change="handleChange()">
    <span>{{ serving?.unit?.name }}</span>
    <span v-if="serving?.unit?.measurementPerUnit?.amount && serving?.unit?.measurementPerUnit?.unit">
      (à {{ serving.unit.measurementPerUnit.amount }} {{ serving.unit.measurementPerUnit.unit }})
    </span>
    <button class="icon icon-plus"
            @click.prevent="handleChange('increment')">
      <svg>
        <use href="#plus" />
      </svg>
    </button>
  </div>
</template>
